import React, { useCallback, useEffect, useState } from 'react';
import {
  Wallet, Plus, Banknote, CopyPlus, BarChart3, FileText, FolderTree, CreditCard, CalendarRange,
  Scale, StickyNote,
} from 'lucide-react';
import { expenseAPI } from '../../services/api';
import { scrollContentToTop } from '../../utils/scroll';
import { useExpenseCategories, DepartmentPills, readDepartment, writeDepartment } from './shared';
import ExpenseDashboard from './ExpenseDashboard';
import ExpenseEntries from './ExpenseEntries';
import ExpenseCategories from './ExpenseCategories';
import ExpenseCheques from './ExpenseCheques';
import ExpenseCashPosition from './ExpenseCashPosition';
import ExpenseNotes from './ExpenseNotes';
import ExpenseMonthlyReport from './ExpenseMonthlyReport';
import ExpenseEntryModal from './ExpenseEntryModal';
import WithdrawalModal from './WithdrawalModal';
import CopyMonthModal from './CopyMonthModal';

// The Expenses module shell: the header with its three ways in (Add expense, Record a withdrawal,
// Copy a month), the department that every figure is scoped to, and the pages. The department
// sticks across pages and visits, the way it did in the reporting app this replaces.

const ALL_TABS = [
  { key: 'dashboard',     label: 'Dashboard',      icon: BarChart3,     perm: 'VIEW_DASHBOARD_TAB' },
  { key: 'entries',       label: 'Entries',        icon: FileText,      perm: 'VIEW_ENTRIES_TAB' },
  { key: 'cash-position', label: 'Cash Position',  icon: Scale,         perm: 'VIEW_DASHBOARD_TAB' },
  { key: 'cheques',       label: 'Cheques',        icon: CreditCard,    perm: 'VIEW_CHEQUES_TAB' },
  { key: 'categories',    label: 'Categories',     icon: FolderTree,    perm: 'VIEW_CATEGORIES_TAB' },
  { key: 'notes',         label: 'Notes',          icon: StickyNote,    perm: 'VIEW_DASHBOARD_TAB' },
  { key: 'monthly',       label: 'Monthly Report', icon: CalendarRange, perm: 'VIEW_DASHBOARD_TAB' },
];

/** Pages whose figures are scoped to the chosen department. */
const SCOPED_TABS = new Set(['dashboard', 'entries', 'cash-position']);

const ExpenseManagement = ({ defaultTab, hasPermission }) => {
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('EXPENSES', action) : true),
    [hasPermission],
  );

  const [activeTab, setActiveTab] = useState(defaultTab || 'dashboard');
  useEffect(() => { if (defaultTab) setActiveTab(defaultTab); }, [defaultTab]);
  useEffect(() => { scrollContentToTop(); }, [activeTab]);

  const cats = useExpenseCategories();
  const [department, setDepartmentState] = useState(readDepartment);
  const setDepartment = useCallback((v) => { setDepartmentState(v); writeDepartment(v); }, []);
  // A remembered department that no longer exists falls back to all.
  useEffect(() => {
    if (cats.loading || department === 'ALL') return;
    if (!cats.roots.some(r => r.id === department)) setDepartment('ALL');
  }, [cats.loading, cats.roots, department, setDepartment]);

  const [meta, setMeta] = useState({ banks: [], bankByDepartment: {} });
  const loadMeta = useCallback(() => {
    expenseAPI.getMeta().then(m => setMeta(m || { banks: [], bankByDepartment: {} })).catch(() => {});
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  // Bumped after every save so whichever page is open reloads its figures.
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => { setRefreshKey(k => k + 1); loadMeta(); }, [loadMeta]);

  const [entryModal, setEntryModal] = useState(null);       // null | { editing: detail|null }
  const [withdrawalModal, setWithdrawalModal] = useState(null);
  const [showCopy, setShowCopy] = useState(false);

  const openAdd = () => setEntryModal({ editing: null });
  const openEdit = async (entry) => {
    try {
      const detail = await expenseAPI.getEntry(entry.id);
      if (detail.entryType === 'WITHDRAWAL') setWithdrawalModal({ editing: detail });
      else setEntryModal({ editing: detail });
    } catch (e) {
      // The row is still on screen; the page's own toast covers a failed load.
    }
  };

  const onEntrySaved = (saved, { keepOpen }) => {
    refresh();
    if (!keepOpen) setEntryModal(null);
  };
  const onWithdrawalSaved = (saved, { keepOpen }) => {
    refresh();
    if (!keepOpen) setWithdrawalModal(null);
  };

  const TABS = ALL_TABS.filter(t => can(t.perm));
  const scoped = SCOPED_TABS.has(activeTab);
  const pageProps = { department, cats, refreshKey, can, onEdit: openEdit, onAdd: openAdd, refresh };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center shadow-lg">
                <Wallet className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Expenses</h1>
                <p className="text-gray-500 mt-0.5 text-sm">What was spent, what was drawn from the bank, and the cash in hand</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {can('RECORD_PAYMENT') && (
                <button
                  onClick={() => setWithdrawalModal({ editing: null })}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  <Banknote className="h-5 w-5 text-blue-600" />
                  Record a withdrawal
                </button>
              )}
              <button
                onClick={() => setShowCopy(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                <CopyPlus className="h-5 w-5 text-gray-500" />
                Copy a month
              </button>
              <button
                onClick={openAdd}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
              >
                <Plus className="h-5 w-5" />
                Add expense
              </button>
            </div>
          </div>
          {scoped && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <DepartmentPills roots={cats.roots} value={department} onChange={setDepartment} />
            </div>
          )}
        </div>

        {/* Tab bar, hidden when the sidebar does the switching. */}
        {!defaultTab && (
          <div className="bg-white rounded-2xl shadow-xl mb-6">
            <div className="flex border-b border-gray-100 overflow-x-auto">
              {TABS.map(tab => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-5 py-4 text-sm font-medium transition-colors relative whitespace-nowrap ${
                      isActive ? 'text-navy-800' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                    {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-navy-800 rounded-t" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'dashboard' && can('VIEW_DASHBOARD_TAB') && <ExpenseDashboard {...pageProps} onGoToEntries={() => setActiveTab('entries')} />}
        {activeTab === 'entries' && can('VIEW_ENTRIES_TAB') && <ExpenseEntries {...pageProps} />}
        {activeTab === 'cash-position' && can('VIEW_DASHBOARD_TAB') && <ExpenseCashPosition {...pageProps} />}
        {activeTab === 'cheques' && can('VIEW_CHEQUES_TAB') && <ExpenseCheques {...pageProps} onRecordWithdrawal={() => setWithdrawalModal({ editing: null })} />}
        {activeTab === 'categories' && can('VIEW_CATEGORIES_TAB') && <ExpenseCategories {...pageProps} />}
        {activeTab === 'notes' && can('VIEW_DASHBOARD_TAB') && <ExpenseNotes />}
        {activeTab === 'monthly' && can('VIEW_DASHBOARD_TAB') && <ExpenseMonthlyReport />}

        {entryModal && (
          <ExpenseEntryModal
            key={entryModal.editing ? entryModal.editing.id : 'new'}
            editing={entryModal.editing}
            cats={cats}
            initialDepartment={department}
            meta={meta}
            onClose={() => setEntryModal(null)}
            onSaved={onEntrySaved}
          />
        )}
        {withdrawalModal && (
          <WithdrawalModal
            key={withdrawalModal.editing ? withdrawalModal.editing.id : 'new'}
            editing={withdrawalModal.editing}
            cats={cats}
            initialDepartment={department}
            meta={meta}
            onClose={() => setWithdrawalModal(null)}
            onSaved={onWithdrawalSaved}
          />
        )}
        {showCopy && (
          <CopyMonthModal
            cats={cats}
            initialDepartment={department}
            onClose={() => setShowCopy(false)}
            onSaved={() => { refresh(); setShowCopy(false); }}
          />
        )}
      </div>
    </div>
  );
};

export default ExpenseManagement;
